import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Tab, Tabs, Typography } from '@mui/material';

const HeaderSlots = createContext<{ actions: HTMLDivElement | null; context: HTMLDivElement | null } | null>(null);

/** Each mounted tab supplies its existing actions and data to the shared header. */
export function PropertiesSectionHeader({ actions, context }: { actions: React.ReactNode; context?: React.ReactNode }) {
  const slots = useContext(HeaderSlots);
  if (!slots) return <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="h4" fontWeight={700}>Properties</Typography>{actions}
    </Box>
    {context && <Typography variant="body2" color="text.secondary">{context}</Typography>}
  </Box>;
  return <>
    {slots.actions && createPortal(actions, slots.actions)}
    {slots.context && createPortal(context, slots.context)}
  </>;
}

export function propertyUpdateAge(value?: string | null): string | null {
  if (!value) return null;
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return null;
  const minutes = Math.max(0, Math.floor((Date.now() - time) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
  return `${Math.floor(minutes / 1440)}d ago`;
}

/** One page title, tab row, and context line; no per-tab title band. */
export default function PropertiesShell({ page, onNavigate, children, canAccessInsights }: {
  page: string; onNavigate: (page: string) => void; children: React.ReactNode; canAccessInsights: boolean;
}) {
  const [actions, setActions] = useState<HTMLDivElement | null>(null);
  const [context, setContext] = useState<HTMLDivElement | null>(null);
  return <HeaderSlots.Provider value={{ actions, context }}><Box>
    <Box data-testid="properties-header" sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight={700}>Properties</Typography>
        <Box ref={setActions} data-testid="properties-header-actions" sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }} />
      </Box>
      <Tabs value={page} onChange={(_, value) => onNavigate(value)} aria-label="Properties sections">
        <Tab value="properties" label="Overview" />
        <Tab value="search" label="Search" />
        {canAccessInsights && <Tab value="insights" label="Insights" />}
      </Tabs>
      <Box ref={setContext} data-testid="properties-header-context" sx={{ color: 'text.secondary', typography: 'body2', '&:not(:empty)': { mt: 1 } }} />
    </Box>
    <Box data-testid="properties-content">{children}</Box>
  </Box></HeaderSlots.Provider>;
}
